import routes from './routes'
import SHA256 from 'crypto-js/sha256'

export const USER_LANDED_SESSION_KEY = 'tm_user_landed_fired'
export const USER_LANDED_PATHS: string[] = [routes.account.login, routes.account.createAccount.live]
export const DASHBOARD_LANDED_SESSION_KEY = 'tm_dashboard_landed_fired'

export type UserLandedInput = {
    pathname: string
    referrer: string
    loggedIn: boolean
    language: string
    country: string
    portalHost: string
    marketSegment?: string | null
}

const normalizePath = (pathname: string): string => pathname.replace(/\/+$/, '') || '/'

const orNA = (value?: string | null): string => (value && value.trim() ? value.trim().toLowerCase() : 'na')

const rawOrNA = (value: string | number | null | undefined): string => {
    const s = value == null ? '' : String(value).trim()
    return s === '' ? 'na' : s
}

export const mapPaymentMethod = (type?: string | null): string => {
    const t = (type || '').trim().toLowerCase()
    if (t === 'cc' || t === 'creditcard' || t === 'credit_card') return 'credit_card'
    if (t.startsWith('bank')) return 'bank_transfer'
    if (t.includes('wallet')) return 'e_wallet'
    return 'na'
}

export const normalizeProcessorName = (displayName?: string | null): string => {
    const s = (displayName || '').trim().toLowerCase().replace(/\s+/g, '_')
    return s || 'na'
}

// SHA-256 of the normalised (trimmed, lowercased) email, or na. Hashing happens
// here so the raw email is never placed on the dataLayer; matches the SHA256
// pattern already used in sentryUser.ts.
const hashEmail = (email?: string | null): string => {
    const normalized = (email || '').trim().toLowerCase()
    return normalized ? SHA256(normalized).toString() : 'na'
}

export const formatRef = (referrer: string, portalHost: string): string => {
    if (!referrer) return 'direct'
    let url: URL
    try {
        url = new URL(referrer)
    } catch {
        return 'direct'
    }
    const host = url.hostname
    if (!host || host === portalHost) return 'direct'
    if (/^(?:www\.)?thinkmarkets\.com$/i.test(host)) {
        const segment = url.pathname.split('/').filter(Boolean)[0]
        return segment ? `${url.origin}/${segment}/` : `${url.origin}/`
    }
    return host.replace(/^www\./i, '')
}

const MARKETING_ORIGIN = 'https://www.thinkmarkets.com'

// The market segment (au/ae/za/uk) arrives as the marketing ?src param and is
// resolved at boot into originSrc. Prefer it: a cross-origin referrer from the
// marketing site has its path (and so the segment) stripped by that site's
// referrer policy, leaving document.referrer origin-only.
export const resolveLandingRef = (referrer: string, portalHost: string, marketSegment?: string | null): string => {
    const seg = (marketSegment || '').trim().toLowerCase()
    if (seg && /^[a-z0-9-]{1,32}$/.test(seg)) return `${MARKETING_ORIGIN}/${seg}/`
    return formatRef(referrer, portalHost).toLowerCase()
}

export const buildUserLandedEvent = (input: UserLandedInput): Record<string, string> | null => {
    const path = normalizePath(input.pathname)
    // Anonymous users must land on a tracked entry page. Logged-in users only pass
    // through the login/create-account route transiently before being redirected to
    // the dashboard, so report the dashboard accounts landing for them instead of the
    // transient entry (a genuine dashboard deep-link keeps its own path).
    if (!input.loggedIn && !USER_LANDED_PATHS.includes(path)) return null
    const landedPath = input.loggedIn && USER_LANDED_PATHS.includes(path)
        ? routes.dashboard.accounts.root
        : path
    return {
        event: 'user_landed',
        lang: orNA(input.language),
        ref: resolveLandingRef(input.referrer, input.portalHost, input.marketSegment),
        page_path: normalizePath(landedPath),
    }
}

export type CreateAccountAction = 'clicked' | 'viewed' | 'submitted'

export type CreateAccountInput = {
    referrer: string
    loggedIn: boolean
    language: string
    portalHost: string
    pathname: string
    email?: string | null
    provider?: LoginProvider
}

// One event for the create-account funnel; the stage is carried on the step param
// (clicked | viewed | submitted). provider is set on submit (portal or social), na
// otherwise; email_hash is the SHA-256 of the entered email where available.
export const buildCreateAccountEvent = (action: CreateAccountAction, input: CreateAccountInput): Record<string, string> => ({
    event: 'create_account',
    action: orNA(action),
    flow: orNA(input.provider),
    lang: orNA(input.language),
    ref: formatRef(input.referrer, input.portalHost).toLowerCase(),
    page_path: normalizePath(input.pathname),
    email_hash: hashEmail(input.email),
})

export type PasswordResetAction = 'started' | 'requested' | 'cancelled'

export type PasswordResetInput = {
    referrer: string
    loggedIn: boolean
    language: string
    country: string
    portalHost: string
    pathname: string
    email?: string | null
}

export const buildPasswordResetEvent = (action: PasswordResetAction, input: PasswordResetInput): Record<string, string> => ({
    event: 'password_reset',
    action: orNA(action),
    lang: orNA(input.language),
    ref: formatRef(input.referrer, input.portalHost).toLowerCase(),
    page_path: normalizePath(input.pathname),
    email_hash: hashEmail(input.email),
})

export type LoginProvider = 'portal' | 'google' | 'apple' | 'facebook'

export type SubmittedStatus = 'clicked' | 'success' | 'failed'

export type SubmittedCode = string | number | null | undefined

export type SigninAction = 'viewed' | 'clicked' | 'submitted'

export type SigninInput = {
    referrer: string
    loggedIn: boolean
    language: string
    country: string
    portalHost: string
    pathname: string
    provider?: LoginProvider
    status?: SubmittedStatus
    code?: SubmittedCode
    userId?: string | number | null
    accountId?: string | number | null
    email?: string | null
}

export const buildSigninEvent = (action: SigninAction, input: SigninInput): Record<string, string> => ({
    event: 'signin',
    action: orNA(action),
    flow: orNA(input.provider),
    status: orNA(input.status),
    code: rawOrNA(input.code),
    lang: orNA(input.language),
    ref: formatRef(input.referrer, input.portalHost).toLowerCase(),
    page_path: normalizePath(input.pathname),
    user_id: rawOrNA(input.userId),
    account_id: rawOrNA(input.accountId),
    email_hash: hashEmail(input.email),
})

export type ErrorEventInput = {
    errorType: string
    errorCode: string
    apiEndpoint: string
    apiMethod: string
    apiModule: string
    apiAction: string
    loggedIn: boolean
    language: string
    pathname: string
    userId: string | number | null | undefined
    accountId: string | number | null | undefined
}

export const buildErrorEvent = (input: ErrorEventInput): Record<string, string> => ({
    event: 'error',
    error_type: orNA(input.errorType),
    error_code: orNA(input.errorCode),
    api_endpoint: orNA(input.apiEndpoint),
    api_method: orNA(input.apiMethod),
    api_module: orNA(input.apiModule),
    api_action: orNA(input.apiAction),
    lang: orNA(input.language),
    ref: 'na',
    page_path: normalizePath(input.pathname),
    user_id: rawOrNA(input.userId),
    account_id: rawOrNA(input.accountId),
})

export type SignoutInput = {
    referrer: string
    loggedIn: boolean
    language: string
    portalHost: string
    pathname: string
    userId?: string | number | null
    accountId?: string | number | null
    email?: string | null
}

export const buildSignoutEvent = (input: SignoutInput): Record<string, string> => ({
    event: 'signout',
    action: 'clicked',
    lang: orNA(input.language),
    ref: formatRef(input.referrer, input.portalHost).toLowerCase(),
    page_path: normalizePath(input.pathname),
    user_id: rawOrNA(input.userId),
    account_id: rawOrNA(input.accountId),
    email_hash: hashEmail(input.email),
})

// ---------------------------------------------------------------------------
// Onboarding (ThinkPortal phase 2, THPRT-6368). Builders are pure: they return a
// dataLayer payload and never touch window. Every event carries the phase-1
// common params (country/lang/user_type/ref/page_path) plus org + flow, and step
// events additionally carry the resolved analytics step.
// ---------------------------------------------------------------------------

export type OnboardingEventStep =
    | 'personal_info'
    | 'phone'
    | 'email'
    | 'address'
    | 'residency'
    | 'tax'
    | 'employment'
    | 'financials'
    | 'declaration'
    | 'entity'
    | 'application_type'
    | 'chess'
    | 'consent'
    | 'appropriateness'
    | 'platform'
    | 'terms'
    | 'submit'
    | 'account_created'
    | 'id_verification'

export type OnboardingAction = 'viewed' | 'submitted' | 'requested_otp' | 'exited' | 'change_email' | 'change_phone' | 'denied' | 'retry' | 'verified' | 'verify_later' | 'otp_failed' | 'exit_to_dashboard' | 'contact_us'

export type AppropriatenessResult = 'pass' | 'refer' | 'fail'

export type OnboardingBaseInput = {
    org: string
    flow: string
    country: string
    countryName?: string | null
    language: string
    loggedIn: boolean
    referrer: string
    portalHost: string
    pathname: string
    simplified?: boolean
    userId?: string | number | null
    nationality?: string | null
    currency?: string | null
    accountType?: string | null
    platform?: string | null
    accountId?: string | number | null
    email?: string | null
    selection?: string | null
    tradingType?: string | null
}

const onboardingCommon = (input: OnboardingBaseInput): Record<string, string> => ({
    org: orNA(input.org),
    flow: orNA(input.flow),
    country: orNA(input.country),
    country_name: orNA(input.countryName),
    lang: orNA(input.language),
    ref: formatRef(input.referrer, input.portalHost).toLowerCase(),
    page_path: normalizePath(input.pathname),
    email_hash: hashEmail(input.email),
    selection: orNA(input.selection),
    user_id: rawOrNA(input.userId),
    nationality: orNA(input.nationality),
    currency: orNA(input.currency),
    account_type: orNA(input.accountType),
    trading_type: orNA(input.tradingType),
    platform: orNA(input.platform),
    platform_type: orNA(input.platform),
    simplified: input.simplified ? 'true' : 'na',
})

export const buildOnboardingStepEvent = (
    step: OnboardingEventStep,
    action: OnboardingAction,
    input: OnboardingBaseInput,
): Record<string, string> => ({
    event: 'onboarding',
    step: orNA(step),
    action: orNA(action),
    ...onboardingCommon(input),
})

export const buildProspectEvent = (input: OnboardingBaseInput): Record<string, string> => ({
    event: 'prospect',
    flow: orNA(input.flow),
    lang: orNA(input.language),
    ref: formatRef(input.referrer, input.portalHost).toLowerCase(),
    page_path: normalizePath(input.pathname),
    simplified: input.simplified ? 'true' : 'na',
})

export const buildLeadEvent = (input: OnboardingBaseInput): Record<string, string> => ({
    event: 'lead',
    ...onboardingCommon(input),
})

export const buildPhoneVerifiedEvent = (input: OnboardingBaseInput): Record<string, string> => ({
    event: 'onboarding',
    step: 'phone',
    action: 'verified',
    ...onboardingCommon(input),
})

export const buildPlatformSelectedEvent = (platform: string | null | undefined, input: OnboardingBaseInput): Record<string, string> => ({
    event: 'onboarding',
    step: 'platform',
    action: 'submitted',
    ...onboardingCommon(input),
    platform_type: orNA(platform),
    platform: orNA(platform),
})

export type TaxIdDeferralEventName =
    | 'tax_id_trigger_clicked'
    | 'tax_id_acknowledgement_ticked'
    | 'tax_id_acknowledgement_unticked'
    | 'tax_information_submitted_deferred'
    | 'tax_information_submitted_full'

export const buildTaxIdDeferralEvent = (name: TaxIdDeferralEventName, input: OnboardingBaseInput): Record<string, string> => ({
    event: name,
    ...onboardingCommon(input),
})

export const buildAppropriatenessResultEvent = (result: AppropriatenessResult, input: OnboardingBaseInput): Record<string, string> => ({
    event: 'onboarding',
    step: 'appropriateness',
    action: 'result',
    result: orNA(result),
    ...onboardingCommon(input),
})

export const buildOnboardingSubmittedEvent = (input: OnboardingBaseInput, level?: string, applicationId?: string | number | null): Record<string, string> => ({
    event: 'onboarding_submitted',
    ...(level ? { level } : {}),
    account_id: rawOrNA(input.accountId),
    application_id: rawOrNA(applicationId),
    platform: orNA(input.platform),
    ...onboardingCommon(input),
})

export const buildKycSubmittedEvent = (docType: string | null | undefined, input: OnboardingBaseInput): Record<string, string> => ({
    event: 'kyc_submitted',
    doc_type: orNA(docType),
    ...onboardingCommon(input),
})

export type DownloadTarget = 'ios' | 'android' | 'desktop'

export type ReportType = 'transactions_history' | 'account_details'

export type DashboardBaseInput = {
    country: string
    countryName?: string | null
    language: string
    loggedIn: boolean
    referrer: string
    portalHost: string
    pathname: string
    userId?: string | number | null
    accountId?: string | number | null
    email?: string | null
    currency?: string | null
    accountType?: string | null
    productTier?: string | null
    platform?: string | null
}

const dashboardCommon = (input: DashboardBaseInput): Record<string, string> => ({
    country: orNA(input.country),
    country_name: orNA(input.countryName),
    lang: orNA(input.language),
    ref: formatRef(input.referrer, input.portalHost).toLowerCase(),
    page_path: normalizePath(input.pathname),
    user_id: rawOrNA(input.userId),
    account_id: rawOrNA(input.accountId),
    email_hash: hashEmail(input.email),
    currency: orNA(input.currency),
    account_type: orNA(input.accountType),
    product_tier: orNA(input.productTier),
    platform: orNA(input.platform),
})

export type AccountCreatedInput = {
    accountId?: string | number | null
    platform?: string | null
    productTier?: string | null
}

export const buildAccountCreatedEvent = (params: AccountCreatedInput, input: DashboardBaseInput): Record<string, string> => ({
    event: 'account_created',
    ...dashboardCommon(input),
    account_id: rawOrNA(params.accountId),
    platform: orNA(params.platform),
    product_tier: orNA(params.productTier),
})

export type ReportDownloadedInput = {
    reportType?: ReportType | null
    format?: string | null
}

export const buildReportDownloadedEvent = (params: ReportDownloadedInput, input: DashboardBaseInput): Record<string, string> => ({
    event: 'report_downloaded',
    ...dashboardCommon(input),
    report_type: orNA(params.reportType),
    format: orNA(params.format),
})

export const buildVerificationPromptViewedEvent = (input: DashboardBaseInput, action: string = 'viewed'): Record<string, string> => ({
    event: 'verification_prompt_viewed',
    ...dashboardCommon(input),
    action: orNA(action),
})

export type BankAccountAddedInput = {
    status?: string | null
}

export const buildBankAccountAddedEvent = (params: BankAccountAddedInput, input: DashboardBaseInput): Record<string, string> => ({
    event: 'bank_account_added',
    ...dashboardCommon(input),
    status: orNA(params.status),
})

export type SupportChannel = 'chat' | 'ticket_request' | 'line' | 'telegram' | 'twitter'

export const buildSupportEvent = (channel: SupportChannel, input: DashboardBaseInput): Record<string, string> => ({
    event: 'support',
    ...dashboardCommon(input),
    channel: orNA(channel),
})

export const buildDashboardLandedEvent = (input: DashboardBaseInput): Record<string, string> => ({
    event: 'dashboard_landed',
    ...dashboardCommon(input),
})

// KYC approved (THPRT-6368). Approval is an asynchronous backend decision the
// portal cannot observe directly, so this is a front-end approximation: fired
// once per user on first observation of an APPROVED application. Carries country
// (the CM360 KYC Approved tag gates on it) plus org and a deterministic event_id
// for downstream de-duplication.
export type KycApprovedInput = DashboardBaseInput & {
    org?: string | null
    simplified?: boolean
    eventId?: string | null
}

const kycApprovedCommon = (input: KycApprovedInput): Record<string, string> => ({
    ...dashboardCommon(input),
    org: orNA(input.org),
    flow: 'na',
    selection: 'na',
    nationality: 'na',
    trading_type: 'na',
    simplified: input.simplified ? 'true' : 'na',
    event_id: rawOrNA(input.eventId),
})

export const buildKycApprovedEvent = (input: KycApprovedInput): Record<string, string> => ({
    event: 'kyc_approved',
    ...kycApprovedCommon(input),
})

// ---------------------------------------------------------------------------
// Consolidated dashboard events (ThinkPortal phase 3 rework). Each event folds
// its lifecycle stages onto a single event name and carries the stage on the
// action param, plus the dashboard common params. Builders are pure and never
// touch window.
// ---------------------------------------------------------------------------

export type DepositEventAction = 'started' | 'submitted' | 'success' | 'failed' | 'incomplete'

export type DepositEventInput = {
    depositType?: string | null
    amount?: string | number | null
    currency?: string | null
    paymentMethod?: string | null
    processor?: string | null
    transactionId?: string | number | null
    depositStatus?: string | null
    accountId?: string | number | null
    accountType?: string | null
    productTier?: string | null
    platform?: string | null
}

export const buildDepositEvent = (action: DepositEventAction, params: DepositEventInput, input: DashboardBaseInput): Record<string, string> => {
    const obj: Record<string, string> = {
        event: 'deposit',
        ...dashboardCommon(input),
        action: orNA(action),
        deposit_type: orNA(params.depositType),
        amount: rawOrNA(params.amount),
        currency: orNA(params.currency),
        payment_method: orNA(params.paymentMethod),
        processor: orNA(params.processor),
        transaction_id: rawOrNA(params.transactionId),
        deposit_status: orNA(params.depositStatus),
    }
    if (params.accountId != null) obj.account_id = rawOrNA(params.accountId)
    if (params.accountType != null) obj.account_type = orNA(params.accountType)
    if (params.productTier != null) obj.product_tier = orNA(params.productTier)
    if (params.platform != null) obj.platform = orNA(params.platform)
    return obj
}

export type WithdrawalEventAction = 'started' | 'submitted'

export type WithdrawalEventInput = {
    withdrawType?: string | null
    amount?: string | number | null
    currency?: string | null
    paymentMethod?: string | null
    transactionId?: string | number | null
}

export const buildWithdrawalEvent = (action: WithdrawalEventAction, params: WithdrawalEventInput, input: DashboardBaseInput): Record<string, string> => ({
    event: 'withdrawal',
    ...dashboardCommon(input),
    action: orNA(action),
    withdraw_type: orNA(params.withdrawType),
    amount: rawOrNA(params.amount),
    currency: orNA(params.currency),
    payment_method: orNA(params.paymentMethod),
    transaction_id: rawOrNA(params.transactionId),
})

export type TransferEventAction = 'started' | 'submitted'

export type TransferEventInput = {
    amount?: string | number | null
    currency?: string | null
    fromAccount?: string | number | null
    toAccount?: string | number | null
    transactionId?: string | number | null
}

export const buildTransferEvent = (action: TransferEventAction, params: TransferEventInput, input: DashboardBaseInput): Record<string, string> => ({
    event: 'transfer',
    ...dashboardCommon(input),
    action: orNA(action),
    amount: rawOrNA(params.amount),
    currency: orNA(params.currency),
    from_account: rawOrNA(params.fromAccount),
    to_account: rawOrNA(params.toAccount),
    transaction_id: rawOrNA(params.transactionId),
})

export type PlatformEventAction = 'launched' | 'downloaded'

export type PlatformEventInput = {
    platform?: string | null
    launchType?: string | null
    downloadTarget?: string | null
}

export const buildPlatformEvent = (action: PlatformEventAction, params: PlatformEventInput, input: DashboardBaseInput): Record<string, string> => ({
    event: 'platform',
    ...dashboardCommon(input),
    action: orNA(action),
    platform: orNA(params.platform),
    launch_type: orNA(params.launchType),
    download_target: orNA(params.downloadTarget),
})

export type ProfileEventInput = {
    section?: string | null
    status?: string | null
    newLang?: string | null
}

export const buildProfileEvent = (params: ProfileEventInput, input: DashboardBaseInput): Record<string, string> => ({
    event: 'profile',
    ...dashboardCommon(input),
    action: 'updated',
    section: orNA(params.section),
    status: orNA(params.status),
    new_lang: orNA(params.newLang),
})

export type ReferralEventAction = 'started' | 'shared'

export const buildReferralEvent = (action: ReferralEventAction, input: DashboardBaseInput): Record<string, string> => ({
    event: 'referral',
    ...dashboardCommon(input),
    action: orNA(action),
})
